import { cn } from '../../lib/utils';

export interface StackedSegmentBarSegment {
  /** Stable id used for `data-testid` and React `key`. */
  id: string;
  /**
   * Width as a percentage of the bar (0..100). Sum of all segments should
   * equal 100; the molecule does not enforce this — callers are responsible.
   */
  percent: number;
  /** Solid CSS color (hex/rgb/hsl) for the segment fill. */
  color: string;
}

/**
 * Size variants:
 * - `md` (default) — 30-px-tall full-width bar with `rounded-[4px]` outer
 *   corners. Used by the Stale & Duplicated widget header bar
 *   (`289:41030`/`289:42459`).
 * - `sm` — 7-px-tall bar with `rounded-[2px]` outer corners, designed for the
 *   inline sensitivity-breakdown cell in the Top Identities by File Access
 *   widget (Figma `5:23671`: 140w × 6.926px tall).
 */
export type StackedSegmentBarSize = 'md' | 'sm';

export interface StackedSegmentBarProps {
  /**
   * 2..N segments rendered left-to-right. Outer corners of the bar are
   * rounded (4-px or 2-px depending on `size`); intermediate boundaries
   * between segments are flat per Figma `289:49378` / `289:41030` (the bar
   * ships as a single flattened SVG in Figma, so a single rounded wrapper
   * with `overflow-hidden` reproduces it precisely).
   */
  segments: ReadonlyArray<StackedSegmentBarSegment>;
  /**
   * Layout variant. Defaults to `'md'` for back-compat with all existing
   * callers (Stale & Duplicated 30px header bar). Set `'sm'` for the inline
   * 7-px sensitivity bar used by the Top Identities by File Access widget
   * (Figma `5:23671`).
   */
  size?: StackedSegmentBarSize;
  /** Optional aria-label so the bar is announced as an image to AT users. */
  'aria-label'?: string;
  className?: string;
}

/**
 * Layout per `size` — height + outer-corner radius. `md` matches the Stale &
 * Duplicated widget header bar; `sm` matches the Top Identities by File
 * Access inline sensitivity bar (Figma `5:23671`).
 */
const SIZE_STYLES = {
  md: 'h-[30px] rounded-[4px]',
  sm: 'h-[7px] rounded-[2px]',
} as const satisfies Readonly<Record<StackedSegmentBarSize, string>>;

/**
 * `StackedSegmentBar` — full-width horizontal bar composed of N adjacent
 * solid-colour segments. Outer corners rounded; intermediate borders flat.
 *
 * Two consumers:
 * - **Stale & Duplicated widget header bar** (`289:41030`/`289:42459`) at
 *   `size='md'` (default): 30-px tall, `rounded-[4px]`.
 * - **Top Identities by File Access inline sensitivity bar** (Figma
 *   `5:23671`) at `size='sm'`: 7-px tall, `rounded-[2px]`, sized into a
 *   140-px-wide cell.
 */
export function StackedSegmentBar({
  segments,
  size = 'md',
  'aria-label': ariaLabel,
  className,
}: StackedSegmentBarProps) {
  return (
    <div
      role="img"
      aria-label={ariaLabel}
      className={cn('flex w-full overflow-hidden', SIZE_STYLES[size], className)}
    >
      {segments.map((seg) => (
        <div
          key={seg.id}
          data-testid={`stacked-segment-${seg.id}`}
          style={{ width: `${seg.percent}%`, backgroundColor: seg.color }}
          className="h-full"
        />
      ))}
    </div>
  );
}

StackedSegmentBar.displayName = 'StackedSegmentBar';
